import { CheckIcon, CopyIcon, DeleteIcon, RefreshIcon } from '@icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'features/components/Button';
import { useGetLatestDataEntryVersionQuery } from 'features/entry-form-v2/data/useGetLatestDataEntryVersionQuery';
import { useLanguage } from 'features/hooks/useLanguage';
import { useSelector } from 'hooks/utils';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { LanguageType } from 'types/enums';
import { Dialog } from '../../component/Dialog';
import { IconButton } from '../../component/IconButton';
import { ROUTE_MAP } from '../../utils/routeMap';
import { useCreateNewPinCodeMutation } from '../data/useCreateNewPinCode';
import { useDeleteSurveyInvitationMutation } from '../data/useDeleteSurveyInvitationMutation';
import { useGetSurveyInvitations } from '../data/useGetSurveyInvitations';

type SurveyInvitation = {
	id: string;
	surveyName: string;
	completed: boolean;
	createdAt: string;
	createdBy: string;
	expiresAt: string;
};

export const SurveyStatusModal = ({ seriesName }: { seriesName: string }) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];
	const queryClient = useQueryClient();

	const params = useParams();
	const projectId = params['projectId'] as string;
	const entryId = params['entryId'] as string;
	const [open, setOpen] = useState(false);

	const latestEntryVersionQuery = useGetLatestDataEntryVersionQuery({
		entryId,
		projectId
	});

	const originalEntryId = latestEntryVersionQuery.data?.entry['original_id'];

	const surveysQuery = useGetSurveyInvitations(
		{
			projectId: parseInt(projectId),
			entryId: originalEntryId!,
			seriesName
		},
		{ enabled: !!originalEntryId }
	);

	if (surveysQuery.isLoading || !surveysQuery.data?.surveyInvitations?.length) {
		return null;
	}

	const surveys: SurveyInvitation[] = surveysQuery.data.surveyInvitations;
	const completed = surveys.filter(s => s.completed).length;
	const total = surveys.length;

	return (
		<>
			<button
				type="button"
				onClick={e => {
					e.stopPropagation();
					setOpen(true);
				}}
				className="bg-primary-500/10 flex items-center justify-center px-2 py-1 rounded-md text-primary-700 font-semibold hover:bg-primary-500/20"
			>
				{completed} / {total} {dictionary.completedIndicator}
			</button>

			<Dialog open={open} onClose={() => setOpen(false)} title={dictionary.surveyStatus}>
				<div className="overflow-x-auto">
					<table className="border-separate border-spacing-0 text-sm">
						<thead className="h-[40px] sticky top-0 bg-white">
							<tr>
								<th className="px-4 text-left text-primary-700 border-t border-b border-l border-gray-100">
									{dictionary.complete}
								</th>
								<th className="px-4 text-left text-primary-700 border-t border-b border-l border-gray-100">
									{dictionary.surveyName}
								</th>
								<th className="px-4 text-left text-primary-700 border-t border-b border-l border-gray-100">
									{dictionary.createdAt}
								</th>
								<th className="px-4 text-left text-primary-700 border-t border-b border-l border-gray-100">
									{dictionary.createdBy}
								</th>
								<th className="px-4 text-left text-primary-700 border-t border-b border-l border-gray-100">
									{dictionary.expiresAt}
								</th>
								<th className="px-4 text-center text-primary-700 border-t border-b border-l border-gray-100">
									{dictionary.copyLink}
								</th>
								<th className="px-4 text-center text-primary-700 border-t border-b border-l border-gray-100">
									{dictionary.newPinCode}
								</th>
								<th className="px-4 text-center text-primary-700 border-t border-b border-l border-r border-gray-100">
									{dictionary.deleteSurvey}
								</th>
							</tr>
						</thead>
						<tbody className="h-[36px]">
							{surveys.map(survey => (
								<tr key={survey.id} className="odd:bg-gray-100 h-[36px]">
									<td className="px-4 py-2 whitespace-nowrap border-b border-l border-gray-300">
										{survey.completed ? dictionary.yes : dictionary.no}
									</td>
									<td className="px-4 py-2 whitespace-nowrap border-b border-l border-gray-300">
										{survey.surveyName}
									</td>
									<td className="px-4 py-2 whitespace-nowrap border-b border-l border-gray-300">
										{DateTime.fromISO(survey.createdAt)
											.setLocale(language)
											.toLocaleString(DateTime.DATETIME_SHORT)}
									</td>
									<td className="px-4 py-2 whitespace-nowrap border-b border-l border-gray-300">
										{survey.createdBy}
									</td>
									<td className="px-4 py-2 whitespace-nowrap border-b border-l border-gray-300">
										{!survey.completed
											? DateTime.fromISO(survey.expiresAt)
													.setLocale(language)
													.toLocaleString(DateTime.DATETIME_SHORT)
											: null}
									</td>
									<td className="px-4 py-0 whitespace-nowrap border-b border-l border-gray-300 text-center">
										{!survey.completed ? (
											<CopyLinkButton
												link={`${
													window.location.origin
												}${ROUTE_MAP.surveys.byId.createPath({
													surveyId: survey.id
												})}`}
											/>
										) : null}
									</td>
									<td className="px-4 py-0 min-w-[100px] whitespace-nowrap border-b border-l border-gray-300 text-center">
										{!survey.completed ? (
											<CreateNewPinCode surveyId={survey.id} />
										) : null}
									</td>
									<td className="px-4 py-0 whitespace-nowrap border-b border-l border-r border-gray-300 text-center">
										{!survey.completed ? (
											<DeleteSurveyInvitation
												surveyId={survey.id}
												surveyName={survey.surveyName}
												onSurveyDeleted={() => {
													queryClient.invalidateQueries({
														queryKey: [
															'surveyInvitations',
															projectId,
															originalEntryId,
															seriesName
														]
													});
												}}
											/>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Dialog>
		</>
	);
};

const CopyLinkButton = ({ link }: { link: string }) => {
	const [copied, setCopied] = useState(false);

	const copyLinkToClipBoard = () => {
		navigator.clipboard
			.writeText(link)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 3000);
			})
			.catch(err => {
				console.error('Failed to copy text: ', err);
			});
	};

	return (
		<IconButton onClick={copyLinkToClipBoard}>
			{!copied ? <CopyIcon className="size-5" /> : <CheckIcon className="size-5" />}
		</IconButton>
	);
};

const CreateNewPinCode = ({ surveyId }: { surveyId: string }) => {
	const [pinCode, setPinCode] = useState<string>('');
	const [copied, setCopied] = useState(false);

	const params = useParams();
	const projectId = params.projectId as string;

	const createNewPinCodeMutation = useCreateNewPinCodeMutation();

	const handleGenerateNewPinCode = async () => {
		const result = await createNewPinCodeMutation.mutateAsync({
			projectId: parseInt(projectId),
			surveyId
		});

		setPinCode(result.pinCode);
	};

	const copyPinCodeToClipboard = () => {
		navigator.clipboard
			.writeText(pinCode)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 3000);
			})
			.catch(err => {
				console.error('Failed to copy pin code: ', err);
			});
	};

	if (pinCode === '') {
		return (
			<IconButton
				onClick={() => {
					handleGenerateNewPinCode();
				}}
				loading={createNewPinCodeMutation.isLoading}
			>
				<RefreshIcon className="size-5" />
			</IconButton>
		);
	}

	return (
		<div className="flex items-center justify-center gap-1">
			<span className="text-sm font-mono">{pinCode}</span>
			<IconButton onClick={copyPinCodeToClipboard}>
				{!copied ? <CopyIcon className="size-5" /> : <CheckIcon className="size-5" />}
			</IconButton>
		</div>
	);
};

const DeleteSurveyInvitation = ({
	surveyId,
	surveyName,
	onSurveyDeleted
}: {
	surveyId: string;
	surveyName: string;
	onSurveyDeleted: () => void;
}) => {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const params = useParams();
	const projectId = params.projectId as string;

	const deleteSurveyInvitationMutation = useDeleteSurveyInvitationMutation();

	const handleDelete = async () => {
		try {
			await deleteSurveyInvitationMutation.mutateAsync({
				projectId: parseInt(projectId),
				surveyId
			});

			onSurveyDeleted();
			setShowConfirmDialog(false);
		} catch (err) {
			console.error('Failed to delete survey invitation: ', err);
		}
	};

	return (
		<>
			<IconButton
				onClick={() => {
					setShowConfirmDialog(true);
				}}
			>
				<DeleteIcon className="size-6 text-error-500" />
			</IconButton>

			<Dialog
				open={showConfirmDialog}
				onClose={() => setShowConfirmDialog(false)}
				title={dictionary.confirmDelete}
			>
				<div className="flex flex-col gap-4 w-[400px]">
					<p className="text-sm">{dictionary.deleteConfirmation(surveyName)}</p>

					<p className="font-bold text-red-500 text-sm">{dictionary.deleteWarning}</p>

					<div className="flex justify-end gap-3">
						<Button
							variant="secondary"
							text={dictionary.cancel}
							onClick={() => setShowConfirmDialog(false)}
						/>
						<Button
							variant="destructive"
							text={dictionary.deleteSurvey}
							onClick={handleDelete}
							loading={deleteSurveyInvitationMutation.isLoading}
						/>
					</div>
				</div>
			</Dialog>
		</>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		completedIndicator: 'completed',
		complete: 'Complete',
		surveyStatus: 'Survey status',
		surveyName: 'Name',
		surveyLink: 'Link',
		createdAt: 'Created at',
		createdBy: 'Created by',
		newPinCode: 'New pin code',
		yes: 'Yes',
		no: 'No',
		copy: 'Copy link',
		copyLink: 'Copy Link',
		createPinCode: 'New Pin',
		expiresAt: 'Pin code expires',
		actions: 'Actions',
		deleting: 'Deleting...',
		deleteSurvey: 'Delete',
		confirmDelete: 'Confirm Delete',
		deleteConfirmation: (surveyName: string) =>
			`Are you sure you want to delete the survey invitation for "${surveyName}"? This action cannot be undone.`,
		deleteWarning:
			'This will permanently delete the survey invitation and remove all associated pin codes.',
		cancel: 'Cancel'
	},
	[LanguageType.Norwegian]: {
		completedIndicator: 'fullført',
		complete: 'Fullført',
		surveyStatus: 'Undersøkelsesstatus',
		surveyName: 'Navn',
		surveyLink: 'Lenke',
		createdAt: 'Opprettet',
		createdBy: 'Opprettet av',
		newPinCode: 'Ny pin-kode',
		yes: 'Ja',
		no: 'Nei',
		copy: 'Kopier lenke',
		copyLink: 'Kopier Lenke',
		createPinCode: 'Lag Pin-kode',
		expiresAt: 'Pin-kode utløper',
		actions: 'Handlinger',
		deleting: 'Sletter...',
		deleteSurvey: 'Slett',
		confirmDelete: 'Bekreft sletting',
		deleteConfirmation: (surveyName: string) =>
			`Er du sikker på at du vil slette undersøkelsesinvitasjonen for "${surveyName}"? Denne handlingen kan ikke angres.`,
		deleteWarning:
			'Dette vil permanent slette undersøkelsesinvitasjonen og fjerne alle tilknyttede pin-koder.',
		cancel: 'Avbryt'
	}
};
